import { inject, provideAppInitializer } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { filter } from 'rxjs';

/** Session key: set before `location.reload()` so a subsequent failure navigates to 404. */
export const CHUNK_RELOAD_SESSION_KEY = 'joii:chunk-reload-attempted';

function messageFromError(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) {
    const base = `${error.name} ${error.message}`;
    const cause = (error as Error & { cause?: unknown }).cause;
    if (cause) return `${base} ${messageFromError(cause)}`;
    return base;
  }
  return String(error);
}

/**
 * True when a lazy route failed because the JS chunk could not be loaded (stale deploy, network, etc.).
 * Kept narrow so resolver/guard failures are not treated as chunk errors.
 */
export function isChunkLoadFailure(error: unknown): boolean {
  const text = messageFromError(error);
  if (!text) return false;

  if (/failed to fetch dynamically imported module/i.test(text)) return true;
  if (/loading chunk [\d]+ failed/i.test(text)) return true;
  if (/import\(\) failed/i.test(text)) return true;
  if (/\bChunkLoadError\b/i.test(text)) return true;

  return false;
}

export interface ChunkLoadRecoveryOptions {
  /** Path segment for the static not-found route (no leading slash). */
  notFoundPath: string;
}

/**
 * On lazy-route chunk load failure: reload once (sessionStorage), then navigate to `notFoundPath`.
 * Clears the reload flag on successful `NavigationEnd`.
 */
export function provideChunkLoadRecovery(options: ChunkLoadRecoveryOptions) {
  return provideAppInitializer(() => {
    const router = inject(Router);
    const notFoundUrl = `/${options.notFoundPath.replace(/^\//, '')}`;

    router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
      } catch {
        /* ignore */
      }
    });

    router.events.pipe(filter((e): e is NavigationError => e instanceof NavigationError)).subscribe((event) => {
      if (!isChunkLoadFailure(event.error)) return;

      let pending: string | null = null;
      try {
        pending = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
      } catch {
        /* ignore */
      }

      if (pending === '1') {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
        } catch {
          /* ignore */
        }

        const raw = event.error;
        const err = raw instanceof Error ? raw : new Error(messageFromError(raw) || 'Chunk load failed after reload');
        Sentry.captureException(err, {
          tags: {
            chunk_load_recovery: 'unrecoverable_after_reload',
          },
          extra: {
            navigationId: event.id,
            attemptedUrl: event.url,
            routerUrl: router.url,
            notFoundUrl,
          },
        });

        void router.navigateByUrl(notFoundUrl, { replaceUrl: true });
        return;
      }

      try {
        sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
      } catch {
        /* ignore */
      }
      location.reload();
    });
  });
}
